import { getLmsActivityPdf, getLmsBlockImage, getLmsBlockPdf } from 'admin/src/fetchers/courses';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

export type LmsBlockMediaRequest = {
  fileId: string;
  blockUuid?: string;
  fileFormat?: string;
};

export type LmsMediaFetchers = {
  getBlockImage: (courseUuid: string | undefined, activityUuid: string, request: LmsBlockMediaRequest) => Promise<Blob>;
  getBlockPdf: (courseUuid: string | undefined, activityUuid: string, request: LmsBlockMediaRequest) => Promise<Blob>;
  getActivityPdf: (courseUuid: string | undefined, activityUuid: string) => Promise<Blob>;
};

type LmsMediaContextValue = {
  courseUuid?: string;
  activityUuid?: string;
  fetchers: LmsMediaFetchers;
};

const lmsMediaFetchers: LmsMediaFetchers = {
  getBlockImage: async (courseUuid, activityUuid, request) => {
    if (!courseUuid) {
      throw new Error('Course UUID is required to load the LMS block image.');
    }
    return getLmsBlockImage(courseUuid, activityUuid, request);
  },
  getBlockPdf: async (courseUuid, activityUuid, request) => {
    if (!courseUuid) {
      throw new Error('Course UUID is required to load the LMS block PDF.');
    }
    return getLmsBlockPdf(courseUuid, activityUuid, request);
  },
  getActivityPdf: async (courseUuid, activityUuid) => {
    if (!courseUuid) {
      throw new Error('Course UUID is required to load the LMS activity PDF.');
    }
    return getLmsActivityPdf(courseUuid, activityUuid);
  },
};

const LmsMediaContext = createContext<LmsMediaContextValue>({ fetchers: lmsMediaFetchers });

export function LmsMediaProvider({
  fetchers,
  courseUuid,
  activityUuid,
  children,
}: {
  fetchers?: LmsMediaFetchers;
  courseUuid?: string;
  activityUuid?: string;
  children: ReactNode;
}) {
  const parent = useContext(LmsMediaContext);
  const value = useMemo(
    () => ({
      courseUuid: courseUuid ?? parent.courseUuid,
      activityUuid: activityUuid ?? parent.activityUuid,
      fetchers: fetchers ?? parent.fetchers,
    }),
    [activityUuid, courseUuid, fetchers, parent.activityUuid, parent.courseUuid, parent.fetchers],
  );

  return <LmsMediaContext.Provider value={value}>{children}</LmsMediaContext.Provider>;
}

export function useLmsMedia() {
  return useContext(LmsMediaContext);
}
